<template>
  <section :class="containerClass">
    <div v-if="!embedded" class="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="text-base font-semibold text-slate-900">评价与反馈</h2>
        <p class="mt-0.5 text-sm text-slate-500">查看接收人对这份日报的评价、评论与补充要求。</p>
      </div>
      <span v-if="statusSummary?.supplementRequested" class="status-badge bg-orange-50 text-orange-700">待补充</span>
    </div>

    <div v-if="statusSummary?.supplementRequested" class="mb-4 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700">
      接收人要求补充日报，请查看下方反馈并补充总结内容。
    </div>

    <div class="space-y-4">
      <div
        ref="evaluationSectionRef"
        class="scroll-mt-4 transition-shadow duration-200"
        :class="evaluationSectionClass"
        data-feedback-section="evaluation"
      >
        <ReportEvaluationSummary :evaluations="evaluations" />
      </div>
      <div
        ref="commentsSectionRef"
        class="scroll-mt-4"
        data-feedback-section="comments"
      >
        <ReportCommentPanel
          :comments="comments"
          :can-submit="canComment"
          :can-request-supplement="false"
          :disabled="submitting"
          :focus-comment-id="focusCommentId"
          @submit="emit('comment', $event)"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import ReportCommentPanel from './ReportCommentPanel.vue'
import ReportEvaluationSummary from './ReportEvaluationSummary.vue'

const props = defineProps({
  report: {
    type: Object,
    default: null
  },
  canComment: {
    type: Boolean,
    default: false
  },
  submitting: {
    type: Boolean,
    default: false
  },
  embedded: {
    type: Boolean,
    default: false
  },
  focusCommentId: {
    type: [String, Number],
    default: ''
  },
  focusTarget: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['comment'])

const evaluationSectionRef = ref(null)
const commentsSectionRef = ref(null)
const comments = computed(() => Array.isArray(props.report?.comments) ? props.report.comments : [])
const evaluations = computed(() => Array.isArray(props.report?.evaluations) ? props.report.evaluations : [])
const statusSummary = computed(() => props.report?.statusSummary || {})
const containerClass = computed(() => props.embedded
  ? 'space-y-4'
  : 'rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:px-5')
const normalizedFocusTarget = computed(() => {
  if (props.focusTarget === 'evaluation') return 'evaluation'
  if (props.focusTarget === 'comments') return 'comments'
  return props.focusCommentId ? 'comments' : ''
})
const evaluationSectionClass = computed(() => normalizedFocusTarget.value === 'evaluation'
  ? 'rounded-xl ring-2 ring-blue-200 ring-offset-2 ring-offset-white'
  : '')

watch(
  () => [normalizedFocusTarget.value, comments.value.length, evaluations.value.length, props.focusCommentId],
  () => scrollFocusedFeedbackTargetIntoView(),
  { immediate: true }
)

function scrollFocusedFeedbackTargetIntoView() {
  nextTick(() => {
    if (normalizedFocusTarget.value === 'evaluation') {
      evaluationSectionRef.value?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      return
    }
    if (normalizedFocusTarget.value === 'comments' && !props.focusCommentId) {
      commentsSectionRef.value?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  })
}
</script>
